/**
 * App Shell
 *
 * Main layout with sidebar navigation and content area.
 * Styled to match echo2 patterns using available DS tokens.
 */

import React from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Icon, Button, Flex, useTheme, spacing, fontSize } from '@fti/design-system';
import { useUser, useProviderLogout } from '../../auth/hooks';
import { useCallerRole } from '../../auth/useCallerRole';
import { GATES, type EdnaRole } from '../../auth/roles';
import { GlobalContextBar } from './GlobalContextBar';

interface NavItem {
  id: string;
  label: string;
  icon?: string;
  /**
   * Minimum role required to see this entry. Omitted means visible to any
   * authenticated user — every read in the API's role map is `standard`.
   */
  minRole?: EdnaRole;
}

/**
 * Sidebar navigation.
 *
 * Note that Monitoring and User Management have DIFFERENT thresholds. They were
 * both a single `adminOnly: true` flag before, which is precisely why that flag
 * could not express the requirement: gating User Management at `engineer` would
 * show it to engineers whose every request then 403s.
 *
 * Air Traffic Control is deliberately ungated — status polling is `standard`, and
 * only its launch/terminate/delete controls require `engineer`.
 */
const NAV_ITEMS: NavItem[] = [
  { id: '/', label: 'Welcome', icon: 'home' },
  { id: '/clients', label: 'Clients' },
  { id: '/matters', label: 'Matters' },
  { id: '/projects', label: 'Projects' },
  { id: '/lexisnexis', label: 'LexisNexis' },
  { id: '/brandwatch', label: 'Brandwatch' },
  { id: '/models', label: 'Models' },
  { id: '/vectorize', label: 'Vectorize' },
  { id: '/atc', label: 'Air Traffic Control' },
  { id: '/monitoring', label: 'Monitoring', icon: 'graph', minRole: GATES.monitoring },
  { id: '/users', label: 'User Management', icon: 'settings', minRole: GATES.userAdmin },
];

export function AppShell() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useUser();
  const { atLeast } = useCallerRole();
  const { signoutRedirect } = useProviderLogout();

  // `atLeast` is false while the role is still loading, so privileged entries stay
  // hidden until it resolves rather than flickering in on every page load.
  const navItems = NAV_ITEMS.filter((item) => !item.minRole || atLeast(item.minRole));

  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;
  const borderColor = theme.colors.border.default;
  const bgPaper = theme.colors.background.paper;
  const bgDefault = theme.colors.background.default;
  const primaryColor = theme.colors.primary[600];

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: bgDefault }}>
      {/* Header */}
      <header style={{
        height: '56px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: `0 ${spacing.lg}`,
        background: bgPaper,
        borderBottom: `1px solid ${borderColor}`,
      }}>
        <div
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: spacing.md }}
          onClick={() => navigate('/')}
        >
          <img src="/fti-logo.svg" alt="FTI Consulting" style={{ height: '28px' }} />
          <Flex align="center" gap="xs">
            <img src="/edna.webp" alt="Edna" style={{ height: '36px', borderRadius: '50%' }} />
            <span style={{ fontSize: '1.1rem', fontWeight: 700, color: textColor, letterSpacing: '0.02em' }}>
              Edna
            </span>
          </Flex>
        </div>
        <Flex align="center" gap="sm">
          {user?.email && (
            <span style={{ fontSize: fontSize.sm, color: mutedColor }}>{user.email}</span>
          )}
          <Button variant="outline" color="error" size="sm" onClick={() => signoutRedirect()}>
            Sign out
          </Button>
        </Flex>
      </header>

      <div style={{ display: 'flex', flex: 1, minHeight: 0, height: 'calc(100vh - 56px)' }}>
        {/* Sidebar */}
        <nav style={{
          width: '220px',
          minWidth: '220px',
          background: bgPaper,
          borderRight: `1px solid ${borderColor}`,
          overflowY: 'auto',
          padding: `${spacing.md} 0`,
        }}>
          {navItems.map((item) => {
            const isActive = location.pathname === item.id;
            return (
              <div
                key={item.id}
                onClick={() => navigate(item.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.sm,
                  padding: `${spacing.xs} ${spacing.md}`,
                  margin: `1px ${spacing.sm}`,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: fontSize.sm,
                  fontWeight: isActive ? 600 : 400,
                  color: isActive ? primaryColor : textColor,
                  background: isActive ? `${primaryColor}10` : 'transparent',
                  transition: 'background 0.15s',
                }}
              >
                {item.icon && <Icon name={item.icon as any} size="sm" />}
                <span style={{ whiteSpace: 'normal' }}>{item.label}</span>
              </div>
            );
          })}
        </nav>

        {/* Main Content */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <GlobalContextBar />
          <main style={{ flex: 1, padding: spacing.lg, overflow: 'auto' }}>
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
